import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Breadcrumbs,
  Link,
  IconButton,
  Tooltip,
  Chip,
  Divider,
  Button
} from '@mui/material';
import {
  Folder as FolderIcon,
  FolderOpen as FolderOpenIcon,
  Description as FileIcon,
  ExpandLess,
  ExpandMore,
  Refresh as RefreshIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { DokumentationService, DokumentationDatei } from '../services/dokumentationService';

interface TreeNode {
  name: string;
  pfad: string;
  groesse?: number;
  children: TreeNode[];
  isFile: boolean;
}

function buildTree(dateien: DokumentationDatei[]): TreeNode[] {
  const root: TreeNode[] = [];

  for (const datei of dateien) {
    if (!datei.pfad) continue;
    const parts = datei.pfad.split('/').filter(Boolean);
    let current = root;

    for (let i = 0; i < parts.length; i++) {
      const part = parts[i];
      const isLast = i === parts.length - 1;
      let existing = current.find((n) => n.name === part);

      if (!existing) {
        existing = {
          name: part,
          pfad: isLast ? datei.pfad : parts.slice(0, i + 1).join('/'),
          groesse: isLast ? datei.groesse : undefined,
          children: [],
          isFile: isLast
        };
        current.push(existing);
      }
      current = existing.children;
    }
  }

  const sortNodes = (nodes: TreeNode[]) => {
    nodes.sort((a, b) => {
      if (a.isFile === b.isFile) return a.name.localeCompare(b.name);
      return a.isFile ? 1 : -1;
    });
    nodes.forEach((n) => sortNodes(n.children));
  };
  sortNodes(root);
  return root;
}

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

const DokumentationPage: React.FC = () => {
  const [dateien, setDateien] = useState<DokumentationDatei[]>([]);
  const [tree, setTree] = useState<TreeNode[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [openFolders, setOpenFolders] = useState<Set<string>>(new Set());
  const [selectedFile, setSelectedFile] = useState<string | null>(null);
  const [fileContent, setFileContent] = useState<string | null>(null);
  const [fileLoading, setFileLoading] = useState(false);
  const [fileError, setFileError] = useState<string | null>(null);

  const loadDateien = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await DokumentationService.getDateiListe();
      setDateien(data);
      setTree(buildTree(data));
    } catch (err: any) {
      console.error('Fehler beim Laden der Dokumentation:', err);
      setError(err.response?.data?.message || 'Fehler beim Laden der Dokumentation');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDateien();
  }, [loadDateien]);

  const toggleFolder = (pfad: string) => {
    setOpenFolders((prev) => {
      const next = new Set(prev);
      if (next.has(pfad)) {
        next.delete(pfad);
      } else {
        next.add(pfad);
      }
      return next;
    });
  };

  const handleFileClick = async (pfad: string) => {
    setSelectedFile(pfad);
    setFileContent(null);
    setFileError(null);
    setFileLoading(true);
    try {
      const content = await DokumentationService.getDateiInhalt(pfad);
      setFileContent(content);
    } catch (err: any) {
      console.error('Fehler beim Laden der Datei:', err);
      setFileError(err.response?.data?.message || 'Fehler beim Laden der Datei');
    } finally {
      setFileLoading(false);
    }
  };

  const handleBack = () => {
    setSelectedFile(null);
    setFileContent(null);
    setFileError(null);
  };

  const renderTreeNode = (node: TreeNode, depth: number = 0) => {
    if (node.isFile) {
      return (
        <ListItemButton
          key={node.pfad}
          sx={{ pl: 2 + depth * 2 }}
          selected={selectedFile === node.pfad}
          onClick={() => handleFileClick(node.pfad)}
        >
          <ListItemIcon sx={{ minWidth: 36 }}>
            <FileIcon fontSize="small" color="action" />
          </ListItemIcon>
          <ListItemText
            primary={node.name}
            primaryTypographyProps={{ variant: 'body2' }}
          />
          {node.groesse !== undefined && (
            <Chip label={formatBytes(node.groesse)} size="small" variant="outlined" sx={{ ml: 1, fontSize: '0.7rem' }} />
          )}
        </ListItemButton>
      );
    }

    const isOpen = openFolders.has(node.pfad);
    return (
      <React.Fragment key={node.pfad}>
        <ListItemButton sx={{ pl: 2 + depth * 2 }} onClick={() => toggleFolder(node.pfad)}>
          <ListItemIcon sx={{ minWidth: 36 }}>
            {isOpen ? <FolderOpenIcon fontSize="small" color="primary" /> : <FolderIcon fontSize="small" color="primary" />}
          </ListItemIcon>
          <ListItemText
            primary={node.name}
            primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
          />
          {isOpen ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
        </ListItemButton>
        <Collapse in={isOpen} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {node.children.map((child) => renderTreeNode(child, depth + 1))}
          </List>
        </Collapse>
      </React.Fragment>
    );
  };

  const renderBreadcrumbs = () => {
    if (!selectedFile) return null;
    const parts = selectedFile.split('/').filter(Boolean);
    return (
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link
          component="button"
          variant="body2"
          underline="hover"
          color="inherit"
          onClick={handleBack}
        >
          Dokumentation
        </Link>
        {parts.map((part, i) => {
          const isLast = i === parts.length - 1;
          return isLast ? (
            <Typography key={i} variant="body2" color="text.primary">
              {part}
            </Typography>
          ) : (
            <Typography key={i} variant="body2" color="inherit">
              {part}
            </Typography>
          );
        })}
      </Breadcrumbs>
    );
  };

  const isMarkdown = selectedFile?.endsWith('.md');

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={1}>
          {selectedFile && (
            <Tooltip title="Zurück zur Übersicht">
              <IconButton onClick={handleBack}>
                <ArrowBackIcon />
              </IconButton>
            </Tooltip>
          )}
          <Typography variant="h4">Dokumentation</Typography>
        </Box>
        <Button onClick={loadDateien} startIcon={<RefreshIcon />}>
          Aktualisieren
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : selectedFile ? (
        <Box>
          {renderBreadcrumbs()}
          <Paper elevation={3} sx={{ p: 0, overflow: 'hidden' }}>
            <Box sx={{ px: 3, py: 1.5, backgroundColor: '#fafafa', borderBottom: '1px solid #e0e0e0' }}>
              <Typography variant="subtitle2" color="text.secondary">
                {selectedFile}
              </Typography>
            </Box>
            <Box sx={{ p: 3 }}>
              {fileLoading ? (
                <Box display="flex" justifyContent="center" py={4}>
                  <CircularProgress />
                </Box>
              ) : fileError ? (
                <Alert severity="error">{fileError}</Alert>
              ) : isMarkdown ? (
                <Box
                  sx={{
                    overflow: 'auto',
                    maxHeight: 'calc(100vh - 300px)',
                    '& h1': { fontSize: '1.8rem', fontWeight: 600, mt: 2, mb: 1, borderBottom: '1px solid #e0e0e0', pb: 1 },
                    '& h2': { fontSize: '1.4rem', fontWeight: 600, mt: 2, mb: 1 },
                    '& h3': { fontSize: '1.15rem', fontWeight: 600, mt: 1.5, mb: 0.5 },
                    '& p': { mb: 1.5, lineHeight: 1.7 },
                    '& ul, & ol': { pl: 3, mb: 1.5 },
                    '& li': { mb: 0.5 },
                    '& code': { backgroundColor: '#f5f5f5', px: 0.5, py: 0.25, borderRadius: 0.5, fontSize: '0.85em', fontFamily: 'monospace' },
                    '& pre': { backgroundColor: '#f5f5f5', p: 2, borderRadius: 1, overflow: 'auto', fontSize: '0.85rem', fontFamily: 'monospace', mb: 2 },
                    '& pre code': { backgroundColor: 'transparent', p: 0 },
                    '& blockquote': { borderLeft: '4px solid #1976d2', pl: 2, ml: 0, color: 'text.secondary', fontStyle: 'italic' },
                    '& table': { borderCollapse: 'collapse', width: '100%', mb: 2 },
                    '& th, & td': { border: '1px solid #e0e0e0', px: 1.5, py: 1, textAlign: 'left' },
                    '& th': { backgroundColor: '#fafafa', fontWeight: 600 },
                    '& a': { color: 'primary.main' },
                    '& hr': { border: 'none', borderTop: '1px solid #e0e0e0', my: 2 },
                  }}
                >
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{fileContent || ''}</ReactMarkdown>
                </Box>
              ) : (
                <Box
                  component="pre"
                  sx={{
                    backgroundColor: '#f5f5f5',
                    p: 2,
                    borderRadius: 1,
                    overflow: 'auto',
                    maxHeight: 'calc(100vh - 300px)',
                    fontSize: '0.85rem',
                    fontFamily: 'monospace',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    m: 0,
                    lineHeight: 1.6
                  }}
                >
                  {fileContent}
                </Box>
              )}
            </Box>
          </Paper>
        </Box>
      ) : (
        <Box display="flex" gap={3}>
          <Paper elevation={3} sx={{ flex: 1, overflow: 'hidden' }}>
            <Box sx={{ px: 3, py: 1.5, backgroundColor: '#fafafa', borderBottom: '1px solid #e0e0e0' }}>
              <Typography variant="subtitle2" color="text.secondary">
                {dateien.length} Datei{dateien.length !== 1 ? 'en' : ''}
              </Typography>
            </Box>
            <List dense disablePadding sx={{ overflow: 'auto', maxHeight: 'calc(100vh - 300px)' }}>
              {tree.map((node) => renderTreeNode(node))}
              {tree.length === 0 && (
                <Box py={4} textAlign="center">
                  <Typography variant="body2" color="text.secondary">
                    Keine Dokumentationsdateien gefunden
                  </Typography>
                </Box>
              )}
            </List>
          </Paper>
        </Box>
      )}
    </Box>
  );
};

export default DokumentationPage;
